import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { t } from '@fetmode/i18n';
import { useAuth } from './AuthProvider.js';
import { buildGoogleOAuthLoginRequest, canUseGoogleOAuthStub, getViteGoogleClientId, isViteDevMode, } from './google-oauth.js';
function createDevStubRequest(provider) {
    const random = Math.random().toString(36).slice(2, 10);
    const slug = provider.toLowerCase();
    return {
        provider,
        stubEmail: `${slug}.dev.${random}@fetmode.local`,
        stubName: `${provider} Dev ${random}`,
    };
}
function GoogleLogo() {
    return (_jsxs("svg", { className: "auth-oauth__logo", viewBox: "0 0 24 24", width: "22", height: "22", "aria-hidden": true, focusable: "false", children: [_jsx("path", { fill: "#4285F4", d: "M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" }), _jsx("path", { fill: "#34A853", d: "M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" }), _jsx("path", { fill: "#FBBC05", d: "M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62Z" }), _jsx("path", { fill: "#EA4335", d: "M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" })] }));
}
function AppleLogo() {
    return (_jsx("svg", { className: "auth-oauth__logo", viewBox: "0 0 24 24", width: "22", height: "22", "aria-hidden": true, focusable: "false", children: _jsx("path", { fill: "currentColor", d: "M16.7 12.66c.03 2.82 2.47 3.76 2.5 3.77-.02.07-.39 1.34-1.29 2.65-.78 1.14-1.58 2.27-2.85 2.3-1.25.02-1.65-.74-3.08-.74-1.43 0-1.87.72-3.05.76-1.22.04-2.15-1.23-2.94-2.36-1.61-2.33-2.84-6.58-1.19-9.45.82-1.43 2.29-2.34 3.88-2.36 1.21-.02 2.35.81 3.08.81.73 0 2.11-.99 3.56-.85.61.03 2.31.24 3.4 1.85-.09.05-2.03 1.18-2.02 3.62ZM14.4 5.55c.65-.79 1.1-1.89.98-2.98-.95.04-2.1.63-2.78 1.43-.61.7-1.15 1.82-1.01 2.89 1.07.08 2.16-.54 2.81-1.34Z" }) }));
}
function XLogo() {
    return (_jsx("svg", { className: "auth-oauth__logo", viewBox: "0 0 24 24", width: "20", height: "20", "aria-hidden": true, focusable: "false", children: _jsx("path", { fill: "currentColor", d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" }) }));
}
const PROVIDERS = [
    { id: 'GOOGLE', labelKey: 'common.auth.continueWithGoogle', Logo: GoogleLogo },
    { id: 'APPLE', labelKey: 'common.auth.continueWithApple', Logo: AppleLogo },
    { id: 'X', labelKey: 'common.auth.continueWithX', Logo: XLogo },
];
export function GoogleContinueButton({ disabled = false, onSuccess, onError, }) {
    const { loginWithOAuth } = useAuth();
    const [submitting, setSubmitting] = useState(null);
    const googleClientId = getViteGoogleClientId();
    const showGoogleStub = canUseGoogleOAuthStub();
    const showExtraStubs = isViteDevMode();
    const googleAvailable = Boolean(googleClientId || showGoogleStub);
    if (!googleAvailable && !showExtraStubs) {
        return null;
    }
    async function runOAuth(provider) {
        setSubmitting(provider);
        try {
            const body = provider === 'GOOGLE'
                ? await buildGoogleOAuthLoginRequest()
                : createDevStubRequest(provider);
            await loginWithOAuth(body);
            onSuccess?.();
        }
        catch {
            onError?.(t('common.auth.oauthError'));
        }
        finally {
            setSubmitting(null);
        }
    }
    function isProviderEnabled(provider) {
        if (provider === 'GOOGLE') {
            return googleAvailable;
        }
        return showExtraStubs;
    }
    return (_jsx("div", { className: "auth-oauth", role: "group", "aria-label": t('common.auth.oauthGroupLabel'), children: PROVIDERS.map(({ id, labelKey, Logo }) => {
            const enabled = isProviderEnabled(id);
            const label = t(labelKey);
            const busy = submitting === id;
            return (_jsxs("button", { type: "button", className: `auth-oauth__btn auth-oauth__btn--${id.toLowerCase()}${busy ? ' auth-oauth__btn--busy' : ''}`, disabled: disabled || submitting !== null || !enabled, "aria-label": label, title: label, onClick: () => {
                    void runOAuth(id);
                }, children: [busy ? _jsx("span", { className: "auth-oauth__spinner", "aria-hidden": true }) : _jsx(Logo, {}), _jsx("span", { className: "auth-oauth__name", children: id === 'X' ? 'X' : id === 'APPLE' ? 'Apple' : 'Google' })] }, id));
        }) }));
}
//# sourceMappingURL=GoogleContinueButton.js.map